import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from '@mui/material';
import { QueryStatus } from '@reduxjs/toolkit/query';
import moment from 'moment';
import { SButton, SIcon } from '@chargebee/sting-react';
import {
  CloseOverlay,
  EditorDrawer,
  TopBar,
} from '@bbkAdminComponents/editor-page-overlay/drawer';
import { useShowDemoData } from '@bbkAdminComponents/play/play-context';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { CreatePlayModal } from '@bbkAdminComponents/unified-plays/create-play-modal';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import { useGetDemoSuffix } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { selectReportFilterForPicker } from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { PlayType } from '@bbkAdminRedux/rtkq/play.slice';
import {
  reportsSlice,
  ReportType,
  type ExportReportArgs,
  type GetReportV2Args,
  type PredictiveCustomerSnapshotData,
  type ReportFilters,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { segmentCustomEvent } from '@bbkAdminUtils/analytics';
import { cs } from '@bbkAdminUtils/css-utils';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { Icons } from '@bbkAdminUtils/icons';
import { useUrl } from '@bbkAdminUtils/nav';
import { useGetProductLicense } from '@bbkAdminUtils/plans';
import { useSearchParams } from '@bbkAdminUtils/useSearchParams';
import {
  useAppLocation,
  useHistory,
  useLocation,
} from '@bbkShared/react-router';
import CardContentArea from '../cards/sections/card-content-area';
import { ExportCustomerDataDialog } from '../export-data/export-customer-data-dialog';
import { PredictiveBrandFilterPicker } from '../reports-filter-picker/reports-filter-picker';

export enum RiskLevel {
  High = 'High Risk',
  Medium = 'Medium Risk',
  Low = 'Low Risk',
}

export const riskClassMap = {
  [RiskLevel.High]:
    'tw-bg-gradient-to-b tw-from-cbrdanger-200 tw-to-cbrdanger-300',
  [RiskLevel.Medium]:
    'tw-bg-gradient-to-b tw-from-cbrwarn-200 tw-to-cbrwarn-300',
  [RiskLevel.Low]:
    'tw-bg-gradient-to-b tw-from-cbryellow-200 tw-to-cbryellow-300',
};
export const getRiskClass = (riskLevel: RiskLevel) =>
  riskClassMap[riskLevel] || '';

const PredictiveCustomerPreview: React.FC = () => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const growthV2License = productLicense.growthV2License;

  const appLocation = useAppLocation();
  const history = useHistory();
  const location = useLocation<{
    riskCategory?: RiskLevel;
  }>();
  const createUrl = useUrl();
  const searchParams = useSearchParams<{
    riskCategory: string;
    returnUrl: string;
  }>();
  const riskCategory =
    searchParams?.riskCategory ||
    location?.state?.riskCategory ||
    RiskLevel.High;

  const [selectedRiskLevel, setSelectedRiskLevel] = useState(
    riskCategory?.split(' ')[0]
  );

  const handleChange = (event: React.ChangeEvent<object>, tabIndex: number) => {
    setActiveTabIndex(tabIndex);
    setSelectedRiskLevel(Object.values(RiskLevel)[tabIndex]?.split(' ')[0]);
  };
  const isPredictiveEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_PREDICTIVE);
  const selectedRiskLevelValue =
    RiskLevel[selectedRiskLevel as keyof typeof RiskLevel];
  useEffect(() => {
    if (selectedRiskLevelValue === RiskLevel.High) {
      setActiveTabIndex(0);
    } else if (selectedRiskLevelValue === RiskLevel.Medium) {
      setActiveTabIndex(1);
    } else if (selectedRiskLevelValue === RiskLevel.Low) {
      setActiveTabIndex(2);
    }
  }, [riskCategory]);

  const isMultiBrand = useFeatureFlags(FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED);
  const appKey = useSelector(selectAppId);

  const timePicker = useSelectDatePicker();
  const { startDate: start, endDate: end } = timePicker;
  const suffix = useGetDemoSuffix();
  const reportFilters = useSelector(selectReportFilterForPicker(suffix));
  const selectedBrandId = reportFilters.brandFilter;
  const showDemoData = useShowDemoData();

  const request = {
    report_type: ReportType.predictive_customer_snapshot,
  };
  const args: GetReportV2Args = {
    appKey,
    start,
    end,
    report_type_requests: [request],
    filters: [
      isMultiBrand && selectedBrandId
        ? [
            {
              field: 'churn_risk_category',
              operator: 'equals',
              value: selectedRiskLevel,
            },
            {
              field: 'brand_id',
              operator: 'equals',
              value: selectedBrandId,
            },
          ]
        : [
            {
              field: 'churn_risk_category',
              operator: 'equals',
              value: selectedRiskLevel,
            },
          ],
    ],
    context: showDemoData ? BBKRequestContext.reports_filter : undefined,
  };

  const opts = {
    skip: !isPredictiveEnabled || !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<PredictiveCustomerSnapshotData>,
  };

  const {
    data: predictiveCustomerSnapshotData,
    isLoading,
    isFetching,
    status,
  } = reportsSlice.endpoints.getReportV2.useQuery(args, opts);

  const filteredData = useMemo(() => {
    if (!predictiveCustomerSnapshotData?.customers) {
      return [];
    }
    const customers = predictiveCustomerSnapshotData.customers;
    return customers.map((customer) => ({
      customerID: customer.customer_id,
      churnScore: customer.churn_risk_score,
      churnRiskLevel: customer.churn_risk_category,
      ...(isMultiBrand && { brand: customer.brand_name }),
      ...(!isMultiBrand && { productFamily: customer.product_family_code }),
    }));
  }, [predictiveCustomerSnapshotData, isMultiBrand]);
  const headerMapping: {
    customerID: string;
    churnScore: string;
    churnRiskLevel: string;
    brand: string;
    productFamily: string;
  } = {
    customerID: 'Customer ID',
    churnScore: 'Customer risk score',
    churnRiskLevel: 'Churn risk level',
    brand: 'Brand',
    productFamily: 'Product family',
  };

  const totalCustomers = predictiveCustomerSnapshotData?.customers?.length || 0;
  const refreshedDate =
    predictiveCustomerSnapshotData?.current_iteration_date || '';
  const currentIterationDate = moment.utc(refreshedDate);
  const isValidDate = currentIterationDate.isValid();
  let formattedCurrentIterationDate = isValidDate
    ? currentIterationDate.format('MM/DD/YYYY')
    : 'N/A';

  const [exportCustomerDataDialogOpen, setExportCustomerDataDialogOpen] =
    useState(false);
  let noData = useMemo(() => totalCustomers === 0, [totalCustomers]);
  if (status === QueryStatus.rejected) {
    formattedCurrentIterationDate = 'NA';
    noData = false;
  }
  const filter_request: ReportFilters = [
    [
      {
        field: 'churn_risk_category',
        operator: 'equals',
        value: selectedRiskLevel,
      },
    ],
  ];
  function isNullOrEmpty(str?: string | null | undefined): boolean {
    return str === undefined || str === null || str.trim() === '';
  }
  if (!isNullOrEmpty(selectedBrandId) && filter_request[0]) {
    filter_request[0].push({
      field: 'brand_id',
      operator: 'equals',
      value: selectedBrandId,
    });
  }

  const app_key = appKey;
  const start_date = start;
  const end_date = end;
  const company_key = useSelector(selectCompanyInternalName);
  const customerExportArgs: ExportReportArgs = {
    app_key,
    company_key,
    start_date,
    end_date,
    reportType: ReportType.predictive_customer_snapshot_export,
    filters: filter_request,
  };
  const [isHovered, setIsHovered] = useState(false);
  const [showCreatePlayModal, setShowCreatePlayModal] = useState(false);

  const customerPreviewReturnUrl = growthV2License
    ? `${AuthorizedPaths.viewPredictiveCustomerPreview}${encodeObjectForQuery({
        riskCategory: selectedRiskLevelValue,
        returnUrl: searchParams?.returnUrl,
      })}`
    : appLocation.appUrl;

  const navigateToPlayParams = {
    riskCategory: selectedRiskLevelValue,
    returnUrl: customerPreviewReturnUrl,
  };

  const navigateToPlay = () => {
    if (growthV2License) {
      // Open the create play modal for unified plays flow
      setShowCreatePlayModal(true);
    } else {
      // Legacy plays navigation
      history.push(
        createUrl(AuthorizedPaths.playNew) +
          encodeObjectForQuery(navigateToPlayParams),
        {
          playType: agsLicense ? PlayType.RETENTION : PlayType.CHURN_PREVENTION,
          brand_id: selectedBrandId,
          riskCategory: selectedRiskLevelValue,
          name: selectedRiskLevelValue,
        }
      );
    }
  };

  const handleClose = () => {
    history.push({
      pathname: createUrl(searchParams.returnUrl),
      state: { riskCategory: selectedRiskLevelValue },
    });
  };
  return (
    isPredictiveEnabled && (
      <EditorDrawer
        open
        drawerStyles={{ overflow: 'hidden' }}
        disableEnforceFocus
      >
        <TopBar className="tw-flex tw-items-center tw-justify-between tw-px-4">
          <div className="tw-flex tw-pl-4 tw-font-semibold">
            <div className="bbk-new-editor-input bbk-new-editor-input-title tw-flex tw-items-center">
              <Tabs
                value={activeTabIndex}
                onChange={handleChange}
                aria-label="risk tabs"
                indicatorColor="primary"
              >
                <Tab
                  label={
                    <span className="tw-flex tw-items-center tw-normal-case tw-text-cbrneutral-900">
                      <span
                        className={cs(
                          'tw-mr-1.5 tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full',
                          riskClassMap[RiskLevel.High]
                        )}
                      />
                      {RiskLevel.High}
                    </span>
                  }
                  value={0}
                />
                <Tab
                  label={
                    <span className="tw-flex tw-items-center tw-normal-case tw-text-cbrneutral-900">
                      <span
                        className={cs(
                          'tw-mr-1.5 tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full',
                          riskClassMap[RiskLevel.Medium]
                        )}
                      />
                      {RiskLevel.Medium}
                    </span>
                  }
                  value={1}
                />
                <Tab
                  label={
                    <span className="tw-flex tw-items-center tw-normal-case tw-text-cbrneutral-900">
                      <span
                        className={cs(
                          'tw-mr-1.5 tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full',
                          riskClassMap[RiskLevel.Low]
                        )}
                      />
                      {RiskLevel.Low}
                    </span>
                  }
                  value={2}
                />
              </Tabs>
            </div>
          </div>
          <div className="tw-ml-auto tw-flex tw-items-center">
            {(agsLicense || growthV2License) && (
              <TippyTooltip
                tippyOptions={{
                  placement: 'left',
                  disabled: selectedBrandId !== undefined && isMultiBrand,
                  visible:
                    isHovered && selectedBrandId === undefined && isMultiBrand,
                }}
                content={
                  selectedBrandId === undefined ? (
                    <span className="tw-font-normal">
                      View one brand to start targeting customers in a
                      play.{' '}
                    </span>
                  ) : null
                }
              >
                <div
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <SButton
                    className={cs(
                      'tw-flex tw-items-center tw-justify-center tw-gap-1 tw-overflow-hidden tw-rounded tw-px-4 tw-py-2.5',
                      {
                        'tw-pointer-events-none tw-opacity-50':
                          selectedBrandId === undefined && isMultiBrand,
                      }
                    )}
                    variant="primary"
                    onClick={navigateToPlay}
                  >
                    <span className="tw-my-auto tw-mr-1 tw-flex tw-aspect-square tw-w-4 tw-shrink-0 tw-self-stretch tw-object-contain">
                      <SIcon name="play" />
                    </span>
                    <span
                      className={cs('tw-my-auto tw-flex tw-self-stretch', {
                        'tw-opacity-50':
                          selectedBrandId === undefined && isMultiBrand,
                      })}
                    >
                      Target customers in a play
                    </span>
                  </SButton>
                </div>
              </TippyTooltip>
            )}
            <CloseOverlay onClick={handleClose} className="tw-ml-4" />
          </div>
        </TopBar>

        <div className="tw-container tw-mx-auto tw-w-full tw-px-4">
          <div className="tw-m-2 tw-flex tw-items-start tw-justify-between">
            <div className="tw-flex tw-flex-col">
              <h3>
                {Icons.people} Customer Preview
                {totalCustomers < 20
                  ? ` ${totalCustomers} of ${totalCustomers}`
                  : ` 20 of ${totalCustomers}`}
              </h3>
              <p>To view full list of the customers, export the CSV</p>
            </div>
            <div className="tw-ml-4 tw-flex tw-flex-col">
              {isMultiBrand && (
                <div className="tw-mb-2 tw-flex tw-items-center">
                  <PredictiveBrandFilterPicker />
                </div>
              )}
              <div className="tw-flex tw-items-center">
                <p className="tw-mr-2">
                  Last refreshed:{' '}
                  {isValidDate ? formattedCurrentIterationDate : 'N/A'}
                </p>

                <SButton
                  variant="neutral-outline"
                  className={cs('brightPurple', {
                    'tw-pointer-events-none tw-opacity-50': totalCustomers == 0,
                  })}
                  icon={
                    <span className={cs('brightPurple')}>
                      <SIcon name="download" />
                    </span>
                  }
                  onClick={() => {
                    segmentCustomEvent('Customers - clicked export');
                    setExportCustomerDataDialogOpen(true);
                  }}
                >
                  Export
                </SButton>

                {exportCustomerDataDialogOpen && (
                  <ExportCustomerDataDialog
                    open={exportCustomerDataDialogOpen}
                    onClose={() => setExportCustomerDataDialogOpen(false)}
                    reportOpts={{
                      ...customerExportArgs,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="tw-max-h-[800px] tw-overflow-y-auto">
            <CardContentArea
              firstLoad={isLoading}
              loading={isFetching}
              customStyle={{ padding: 0 }}
            >
              {!noData && (
                <TableContainer>
                  <Table className="tw-mt-6 tw-bg-white">
                    <TableHead>
                      <TableRow className="tw-flex tw-w-full tw-pb-3 tw-pl-5 tw-pt-5">
                        {filteredData.length > 0 &&
                          filteredData[0] &&
                          Object.keys(filteredData[0]).map((key) => (
                            <TableCell>
                              {headerMapping[
                                key as keyof typeof headerMapping
                              ] || key.charAt(0).toUpperCase() + key.slice(1)}
                            </TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.map((customer) => (
                        <TableRow className="tw-flex tw-w-full tw-pb-3 tw-pl-5 tw-pt-5">
                          {Object.keys(customer).map((key) => (
                            <TableCell>
                              {key === 'churnRiskLevel' ? (
                                <span className="circle-container">
                                  {customer[key]}
                                  <span
                                    className={`tw-ml-1.5 tw-inline-block tw-h-2.5 tw-w-2.5 tw-rounded-full ${getRiskClass(`${customer[key]} Risk` as RiskLevel)}`}
                                  />
                                </span>
                              ) : (
                                customer[key as keyof typeof customer]
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContentArea>
          </div>
        </div>

        {growthV2License && (
          <CreatePlayModal
            isOpen={showCreatePlayModal}
            onClose={setShowCreatePlayModal}
            routePlayType="retention"
            additionalState={{
              riskCategory: selectedRiskLevelValue,
              name: selectedRiskLevelValue,
              returnUrl: customerPreviewReturnUrl,
            }}
          />
        )}
      </EditorDrawer>
    )
  );
};

export default PredictiveCustomerPreview;
